import { useRef, useState, useEffect } from "react";
import {
  PlayCircleOutline as PlayIcon,
  MoreVert as MenuIcon,
} from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import { Menu, MenuItem, IconButton } from "@mui/material";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 0,
    position: "relative",
  },
  player: {
    width: "100%",
    maxWidth: "100%",
    backgroundColor: "#000",
    borderRadius: 8,
  },
  caption: {
    top: 0,
    position: "absolute",
    left: 0,
    right: 0,
    textAlign: "center",
    background:
      "linear-gradient(to bottom, rgba(0,0,0,0.85) 10%, rgba(0,0,0,0) 100%)",
    paddingBottom: theme.spacing(2),
    color: "#fff",
    display: "flex",
    alignItems: "center",
  },
  cationLabel: { flexGrow: 1 },
  menuLeft: { width: "34px" },
  playIconOverlay: {
    display: "grid",
    placeItems: "center",
    top: "30%",
    bottom: "30%",
    left: "35%",
    right: "35%",
    position: "absolute",
    textAlign: "center",
    background: "rgba(0,0,0,0.85)",
    borderRadius: theme.spacing(2),
    cursor: "pointer",
    zIndex: 1,
    color: "#fff",
    "&:hover": {
      background: `radial-gradient(#2e2e2e, rgba(0,0,0,0.85))`,
    },
  },
  playIcon: { width: "90%", height: "90%" },
}));

const LiveVideoWebSocketCmsv6 = ({
  idx,
  label,
  sources = [],
  onSetCameraLocationClick,
  onPlayClick,
  onStopClick,
  play,
}) => {
  const classes = useStyles();

  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [ready, setReady] = useState(false);

  const [videoWH, setVideoWH] = useState({ w: 0, h: 0 });

  const wsUrl = sources.find((x) => x.type === "websocket")?.source;
  const containerId = `cmsv6_${idx}`;

  const calcVideoWH = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const rect = wrapper.getBoundingClientRect();
    const w = Math.floor(rect.width);
    const h = Math.max(1, Math.round(w / (16 / 9)));

    setVideoWH((prev) => (prev.w !== w || prev.h !== h ? { w, h } : prev));
  };

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    let frame = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(calcVideoWH);
    });

    ro.observe(wrapper);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    if (ready || !videoWH.h) return;

    let cancelled = false;
    try {
      const Cmsv6Player = window.Cmsv6Player;
      if (!Cmsv6Player) return;

      if (typeof window.setCmsV6WasmPath === "function") {
        try {
          window.setCmsV6WasmPath("/libcmsv6decode.wasm");
        } catch {}
      }

      const el = containerRef.current;
      if (!el) return;

      const player = new Cmsv6Player({
        domId: el.id || containerId,
        container: el,
        playerType: "h5",
        isVodMode: 0,
        height: videoWH.h,
        width: videoWH.w,
      });

      if (typeof player.setMute === "function") {
        try {
          player.setMute(true);
        } catch {}
      }

      if (!cancelled) {
        playerRef.current = player;
        setReady(true);
      }
    } catch (e) {
      console.error("init error:", e);
    }

    return () => {
      cancelled = true;
    };
  }, [videoWH.h, containerId, ready]);

  useEffect(() => {
    const player = playerRef.current;
    if (!ready || !player) return;
    player.startVodM(wsUrl, "0");

    if (window.location?.protocol === "https:" && wsUrl.startsWith("ws://")) {
      console.warn("Mixed content ");
      return;
    }
    try {
      player.startVodM(wsUrl, "0");
      play ? player.startVodM(wsUrl, "0") : player.stopVodM();
    } catch (e) {
      console.error("player error:", e);
    }
  }, [play, wsUrl, ready]);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  const handleMenuButtonClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleCameraLocationClick = () => {
    setMenuAnchorEl(null);
    onSetCameraLocationClick();
  };

  return (
    <figure className={classes.root} ref={wrapperRef}>
      <div
        className={classes.player}
        id={containerId}
        ref={containerRef}
        style={{
          width: videoWH.w,
          height: videoWH.h,
          background: "#343434",
        }}
      />

      {!play && (
        <div className={classes.playIconOverlay} onClick={onPlayClick}>
          <PlayIcon className={classes.playIcon} />
        </div>
      )}

      <figcaption className={classes.caption}>
        <div style={{ width: 34 }} />
        <div className={classes.cationLabel}>{label}</div>
        <div>
          <IconButton
            color="inherit"
            aria-controls={menuOpen ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? "true" : undefined}
            onClick={handleMenuButtonClick}
            size="small"
          >
            <MenuIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            MenuListProps={{ "aria-labelledby": "basic-button" }}
          >
            <MenuItem onClick={handleCameraLocationClick}>
              Rename camera
            </MenuItem>
            {play && (
              <MenuItem onClick={() => onStopClick?.()}>Stop</MenuItem>
            )}
          </Menu>
        </div>
      </figcaption>
    </figure>
  );
};

export default LiveVideoWebSocketCmsv6;
